import type { AccessDeviceRegistryItem } from '@/api/core/access-device-registry';
import { canWriteProperties } from '@/api/core/authorization';
import { saveClueDevice, type ClueDeviceWrite } from '@/api/core/clue-device';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useRef, useState } from 'react';
import type { PropertyRuntime } from '../property/property-queries';

/** Clue 장비는 gateway 없이 등록하고 기존 장비는 현재 revision으로 교체한다. */
export function ClueDeviceForm({
  runtime,
  generation,
  device,
  onClose,
  refresh,
}: {
  runtime: PropertyRuntime;
  generation: number;
  device?: AccessDeviceRegistryItem;
  onClose: () => void;
  refresh: () => Promise<unknown>;
}) {
  const [property, setProperty] = useState(device?.registration_property_id ?? '');
  const [deviceId, setDeviceId] = useState(device?.device_id ?? '');
  const [name, setName] = useState(device?.device_name ?? '');
  const [purpose, setPurpose] = useState<ClueDeviceWrite['purpose']>(
    device?.purpose === 'ELEVATOR' || device?.purpose === 'SPACE' || device?.purpose === 'MODULE'
      ? device.purpose
      : 'COMMON_ENTRANCE',
  );
  const [floor, setFloor] = useState(device?.floor?.toString() ?? '');
  const [floors, setFloors] = useState(device?.elevator_floors?.join(',') ?? '');
  const [active, setActive] = useState(device?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const attempt = useRef<{ key: string; commandId: string } | null>(null);
  const save = async () => {
    const actor = runtime.session.getSnapshot();
    if (
      busy ||
      actor.generation !== generation ||
      actor.status !== 'authorized' ||
      !actor.authorization ||
      actor.deniedDomains.includes('property') ||
      !canWriteProperties(actor.authorization)
    )
      return;
    const level = floor.trim() ? Number(floor) : null;
    const levels = floors.trim() ? floors.split(',').map((x) => Number(x.trim())) : null;
    if (
      !isUuid(property) ||
      !deviceId.trim() ||
      deviceId.length > 50 ||
      (level !== null && !Number.isSafeInteger(level)) ||
      (purpose === 'ELEVATOR' && (!levels || levels.some((x) => !Number.isSafeInteger(x))))
    ) {
      setMessage('지점, 기기 식별자와 층을 확인해 주세요.');
      return;
    }
    const key = JSON.stringify([
      property,
      device?.core_device_id,
      device?.revision,
      deviceId,
      active,
      purpose,
      level,
      levels,
      name,
    ]);
    if (attempt.current?.key !== key) attempt.current = { key, commandId: crypto.randomUUID() };
    const body: ClueDeviceWrite = {
      command_id: attempt.current.commandId,
      provider: 'CLUE',
      device_id: deviceId,
      is_active: active,
      purpose,
      floor: level,
      elevator_floors: purpose === 'ELEVATOR' ? levels : null,
      device_name: name.trim() || null,
      ...(device ? { expected_revision: device.revision } : {}),
    };
    setBusy(true);
    setMessage('');
    try {
      await saveClueDevice(runtime.client, property, device?.core_device_id ?? null, body);
      attempt.current = null;
      if (runtime.session.getSnapshot().generation !== generation) return;
      setMessage('기기를 저장했습니다.');
      await refresh();
    } catch (error) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('property', generation);
      setMessage(
        isApiError(error) && error.status === 409
          ? '기기가 변경되었거나 동기화 기기로 수정할 수 없습니다. 다시 조회해 주세요.'
          : '기기를 저장하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-label="Clue 기기" className="space-y-2">
      <h3>{device ? 'Clue 기기 수정' : 'Clue 기기 등록'}</h3>
      {device ? <p>동기화 기기는 서버에서 수정을 거절합니다.</p> : null}
      <label>
        지점 UUID
        <Input
          value={property}
          disabled={!!device}
          onChange={(e) => {
            setProperty(e.target.value);
          }}
        />
      </label>
      <label>
        기기 식별자
        <Input
          value={deviceId}
          onChange={(e) => {
            setDeviceId(e.target.value);
          }}
        />
      </label>
      <label>
        기기 이름
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </label>
      <label>
        기기 층
        <Input
          value={floor}
          onChange={(e) => {
            setFloor(e.target.value);
          }}
        />
      </label>
      <span>용도</span>
      <Select
        value={purpose}
        onValueChange={(v) => {
          if (v) setPurpose(v);
        }}
      >
        <SelectTrigger aria-label="기기 용도">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="COMMON_ENTRANCE">공동 출입문</SelectItem>
          <SelectItem value="ELEVATOR">엘리베이터</SelectItem>
          <SelectItem value="SPACE">공간</SelectItem>
          <SelectItem value="MODULE">모듈</SelectItem>
        </SelectContent>
      </Select>
      {purpose === 'ELEVATOR' ? (
        <label>
          운행 층 (쉼표 구분)
          <Input
            value={floors}
            onChange={(e) => {
              setFloors(e.target.value);
            }}
          />
        </label>
      ) : null}
      <label>
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => {
            setActive(e.target.checked);
          }}
        />{' '}
        기기 활성
      </label>
      <div>
        <Button disabled={busy} onClick={() => void save()}>
          Clue 기기 저장
        </Button>
        <Button variant="outline" onClick={onClose}>
          닫기
        </Button>
      </div>
      {message ? <p role="status">{message}</p> : null}
    </section>
  );
}
